import {
  useDashboardSummary,
  useProperties,
  type DashboardAlert,
} from '@/api/hooks';
import { EventListItem } from '@/components/events/EventListItem';
import { Alert } from '@/components/ui/Alert';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { cn } from '@/lib/cn';
import { formatDate, movementTypeLabel } from '@/lib/format';
import { canManage } from '@/lib/rbac';
import {
  ArrowLeftRight,
  Baby,
  Layers,
  Loader,
  Plus,
  Scale,
  Tags,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export function DashboardPage() {
  const { user } = useAuth();
  const { propertyId } = usePropertyScope();
  const { data: properties } = useProperties();
  const { data, isLoading, isError, error } = useDashboardSummary(propertyId);

  const propertyName = properties?.properties.find((p) => p.id === propertyId)?.name;
  const counts = data?.counts;
  const manager = user ? canManage(user.role) : false;

  return (
    <div>
      <PageHeader
        title={`Olá, ${user?.name.split(' ')[0]}`}
        subtitle={
          propertyName
            ? `Propriedade: ${propertyName}`
            : 'Selecione uma propriedade para começar'
        }
        actions={
          propertyId ? (
            <div className="flex flex-wrap gap-2">
              <Link
                to="/eventos"
                className="inline-flex items-center gap-1.5 rounded-xl bg-manejai-forest px-3 py-2 text-sm font-medium text-white hover:bg-manejai-moss"
              >
                <Plus className="h-4 w-4" />
                Registrar evento
              </Link>
              <Link
                to="/movimentacoes"
                className="inline-flex items-center gap-1.5 rounded-xl border border-manejai-sand bg-white px-3 py-2 text-sm font-medium text-manejai-forest hover:bg-manejai-cream/50"
              >
                <ArrowLeftRight className="h-4 w-4" />
                Movimentação
              </Link>
              <Link
                to="/lotes"
                className="inline-flex items-center gap-1.5 rounded-xl border border-manejai-sand bg-white px-3 py-2 text-sm font-medium text-manejai-forest hover:bg-manejai-cream/50"
              >
                <Layers className="h-4 w-4" />
                Lotes
              </Link>
            </div>
          ) : undefined
        }
      />

      {!propertyId ? (
        <Card>
          <CardBody className="py-12 text-center text-manejai-bark/70">
            Cadastre ou selecione uma propriedade para ver o resumo do rebanho.
          </CardBody>
        </Card>
      ) : isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 rounded-2xl bg-manejai-sand/40 animate-pulse" />
          ))}
        </div>
      ) : isError ? (
        <Alert variant="error" className="mb-6">
          {error instanceof Error ? error.message : 'Não foi possível carregar o painel.'}
        </Alert>
      ) : (
        <>
          {(data?.alerts.length ?? 0) > 0 && (
            <div className="mb-6 space-y-3">
              {data!.alerts.map((alert) => (
                <DashboardAlertRow key={alert.type} alert={alert} canResolveConflicts={manager} />
              ))}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            <StatCard
              icon={Tags}
              label="Animais ativos"
              value={counts?.animals_active ?? 0}
              hint={
                counts
                  ? `${counts.animals_female} ♀ · ${counts.animals_male} ♂`
                  : undefined
              }
              to="/animais"
            />
            <StatCard
              icon={Loader}
              label="Fêmeas prenhes"
              value={counts?.pregnant ?? 0}
              hint={
                counts && counts.calving_next_30_days > 0
                  ? `${counts.calving_next_30_days} parto(s) em 30 dias`
                  : undefined
              }
              to="/reproducao"
            />
            <StatCard
              icon={Baby}
              label="Partos em 7 dias"
              value={counts?.calving_next_7_days ?? 0}
              to="/reproducao"
            />
            <StatCard
              icon={Scale}
              label="Pesagens atrasadas"
              value={counts?.weighing_overdue ?? 0}
              hint="Sem pesagem há +90 dias"
              to="/animais"
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2 mb-8">
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-display font-semibold text-manejai-forest">
                  Cabeças por lote
                </h2>
                <Link to="/lotes" className="text-sm text-manejai-moss hover:underline">
                  Ver lotes
                </Link>
              </CardHeader>
              <CardBody className="p-0">
                {(data?.lots.length ?? 0) === 0 ? (
                  <p className="px-5 py-8 text-sm text-manejai-bark/60 text-center">
                    Nenhum lote ativo com animais.
                  </p>
                ) : (
                  <ul className="divide-y divide-manejai-sand/60">
                    {data!.lots.map((lot) => (
                      <li key={lot.id}>
                        <Link
                          to="/lotes"
                          className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-manejai-cream/40 transition-colors"
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-manejai-forest truncate">{lot.name}</p>
                            {lot.code && (
                              <p className="text-xs text-manejai-bark/50">{lot.code}</p>
                            )}
                          </div>
                          <span className="shrink-0 rounded-full bg-manejai-sand/70 px-2.5 py-1 text-sm font-semibold text-manejai-forest">
                            {lot.active_animals_count}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-display font-semibold text-manejai-forest">
                  Partos previstos (30 dias)
                </h2>
                <Link to="/reproducao" className="text-sm text-manejai-moss hover:underline">
                  Reprodução
                </Link>
              </CardHeader>
              <CardBody className="p-0">
                {(data?.calving_soon.length ?? 0) === 0 ? (
                  <p className="px-5 py-8 text-sm text-manejai-bark/60 text-center">
                    Nenhum parto previsto no período.
                  </p>
                ) : (
                  <ul className="divide-y divide-manejai-sand/60">
                    {data!.calving_soon.map((row) => (
                      <li key={row.cow_id}>
                        <Link
                          to={`/animais/${row.cow_id}`}
                          className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-manejai-cream/40 transition-colors"
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-manejai-forest truncate">
                              {row.cow_internal_number}
                              {row.cow_name ? ` — ${row.cow_name}` : ''}
                            </p>
                            <p className="text-xs text-manejai-bark/55">
                              Previsto: {formatDate(row.expected_calving_date)}
                            </p>
                          </div>
                          {row.within_7_days && (
                            <span className="shrink-0 rounded-full bg-manejai-clay/15 px-2 py-0.5 text-[11px] font-medium text-manejai-clay">
                              7 dias
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-display font-semibold text-manejai-forest">Últimos eventos</h2>
                <Link to="/eventos" className="text-sm text-manejai-moss hover:underline">
                  Ver todos
                </Link>
              </CardHeader>
              <CardBody className="p-0">
                {(data?.recent_events.length ?? 0) === 0 ? (
                  <p className="px-5 py-8 text-sm text-manejai-bark/60 text-center">
                    Nenhum evento registrado ainda.
                  </p>
                ) : (
                  <ul className="divide-y divide-manejai-sand/60">
                    {data!.recent_events.map((ev) => (
                      <EventListItem key={ev.event_id} event={ev} />
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-display font-semibold text-manejai-forest">
                  Últimas movimentações
                </h2>
                <Link to="/movimentacoes" className="text-sm text-manejai-moss hover:underline">
                  Ver todas
                </Link>
              </CardHeader>
              <CardBody className="p-0">
                {(data?.recent_movements.length ?? 0) === 0 ? (
                  <p className="px-5 py-8 text-sm text-manejai-bark/60 text-center">
                    Nenhuma movimentação recente.
                  </p>
                ) : (
                  <ul className="divide-y divide-manejai-sand/60">
                    {data!.recent_movements.map((m) => (
                      <li key={m.id} className="flex items-start gap-3 px-5 py-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-manejai-sand/60 text-manejai-forest">
                          <ArrowLeftRight className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-manejai-forest">
                            {movementTypeLabel(m.movement_type)}
                          </p>
                          <p className="text-sm text-manejai-bark/60">
                            {formatDate(m.movement_date)}
                            {m.notes ? ` · ${m.notes}` : ''}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function DashboardAlertRow({
  alert,
  canResolveConflicts,
}: {
  alert: DashboardAlert;
  canResolveConflicts: boolean;
}) {
  const variant =
    alert.severity === 'warning' ? 'warning' : alert.severity === 'error' ? 'error' : 'info';

  if (alert.type === 'sync_conflict' && !canResolveConflicts) {
    return (
      <Alert variant={variant} title={alert.title}>
        Informe o proprietário ou gerente — {alert.body}
      </Alert>
    );
  }

  return (
    <Alert variant={variant} title={alert.title}>
      {alert.body}{' '}
      <Link to={alert.action_url} className="font-medium underline">
        Abrir
      </Link>
    </Alert>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  to,
}: {
  icon: typeof Tags;
  label: string;
  value: number | string;
  hint?: string;
  to?: string;
}) {
  const inner = (
    <CardBody className="flex items-start gap-4">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-manejai-sand/60 text-manejai-moss">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-manejai-bark/60">{label}</p>
        <p className="font-display text-2xl font-semibold text-manejai-forest">{value}</p>
        {hint && <p className="text-[11px] text-manejai-bark/50 mt-0.5 truncate">{hint}</p>}
      </div>
    </CardBody>
  );

  if (!to) return <Card>{inner}</Card>;

  return (
    <Link to={to} className="block transition-opacity hover:opacity-90">
      <Card className={cn('h-full')}>{inner}</Card>
    </Link>
  );
}
